import React from "react";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import FlowActionButton from "@components/FlowActionButtons/FlowActionButton";
import {
  isHSEWasteDisposalSlops,
  isHSEWasteDisposalSludge,
} from "@features/vessel-call/vessel-call-status/PortServices/flow/hse/utils";
import {
  isBunkering,
  isDebunkering,
  isDeSloping,
  isWasteDisposalGarbage,
  isWasteDisposalSlops,
} from "@features/vessel-call/vessel-call-status/PortServices/flow/utils";
import { isShipChandlingCategory } from "@features/vessel-call/vessel-call-status/PortServices/form/utils";
import { STATUS_KEYS } from "@features/vessel-call/vessel-call-status/PortServices/preview/utils";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { isLoadingAndOffloadingType } from "./ship-chandling/utils";
import { isShipToShipType } from "./ship-to-ship/utils";

interface UpdatePortServiceProps {
  portServiceData?: {
    id: string;
    status: string;
    serviceCategory: string;
    serviceType?: string;
    serviceSubType?: string;
  };
  openUpdateDrawer: (row: any) => void;
}

const LANG_BASE = "vessel-call.port-services";
const NON_UPDATABLE_STATUSES = [
  STATUS_KEYS.COMPLETED,
  STATUS_KEYS.CLOSED,
  STATUS_KEYS.CANCELLED,
  STATUS_KEYS.REJECTED,
];

const BUNKERING_UPDATABLE_STATUSES = [
  STATUS_KEYS.PENDING_APPROVAL,
  STATUS_KEYS.REQUESTED,
  "PENDING",
  STATUS_KEYS.MD_APPROVED,
  "APPROVED_BY_MARINE_DEPARTMENT",
  STATUS_KEYS.OPERATIONS_APPROVED,
  "APPROVED_BY_OPERATIONS",
  STATUS_KEYS.STARTED,
];

const normalizeStatus = (status?: string) =>
  status
    ?.trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
const isShippingAgentRole = (role?: string) =>
  ["shipping-agent", "shipping_agent", "shipping agent"].includes(role?.toLowerCase() ?? "");

const UpdatePortService: React.FC<UpdatePortServiceProps> = ({
  portServiceData,
  openUpdateDrawer,
}) => {
  const { userType, isShippingAgentUser, isOperationsUser, user } = useAuthStore();

  if (!portServiceData) {
    return null;
  }

  const currentStatus = normalizeStatus(portServiceData.status);
  const isBunkeringService = isBunkering(portServiceData.serviceCategory ?? "");
  const isDebunkeringService = isDebunkering(portServiceData.serviceCategory ?? "");
  const isDeSlopingService = isDeSloping(portServiceData.serviceCategory ?? "");
  const isShippingAgent =
    userType === "Shipping Agent" || isShippingAgentUser || user?.roles?.some(isShippingAgentRole);
  const isBunkeringActionAvailable =
    (isBunkeringService || isDebunkeringService || isDeSlopingService) &&
    isShippingAgent &&
    BUNKERING_UPDATABLE_STATUSES.includes(currentStatus ?? "");
  const isShipChandlingWorkflowService = isShipChandlingCategory(
    portServiceData.serviceCategory ?? "",
  );
  const isShipChandlingActionAvailable =
    isShipChandlingWorkflowService &&
    isOperationsUser &&
    BUNKERING_UPDATABLE_STATUSES.includes(currentStatus ?? "");

  const canServiceBeUpdated =
    isWasteDisposalGarbage(
      portServiceData.serviceCategory ?? "",
      portServiceData.serviceType ?? "",
    ) ||
    isWasteDisposalSlops(
      portServiceData.serviceCategory ?? "",
      portServiceData.serviceType ?? "",
    ) ||
    isHSEWasteDisposalSludge(
      portServiceData.serviceCategory ?? "",
      portServiceData.serviceType ?? "",
      portServiceData.serviceSubType ?? "",
    ) ||
    isHSEWasteDisposalSlops(
      portServiceData.serviceCategory ?? "",
      portServiceData.serviceType ?? "",
      portServiceData.serviceSubType ?? "",
    ) ||
    isShipToShipType(portServiceData.serviceCategory ?? "", portServiceData.serviceType ?? "") ||
    (isLoadingAndOffloadingType(
      portServiceData.serviceCategory ?? "",
      portServiceData.serviceType ?? "",
    ) &&
      !isShippingAgent);

  const isNonOperationsServiceUpdateAvailable =
    !isOperationsUser &&
    canServiceBeUpdated &&
    !NON_UPDATABLE_STATUSES.includes(currentStatus ?? "");

  const isActionAvailable =
    isShipChandlingActionAvailable ||
    isBunkeringActionAvailable ||
    isNonOperationsServiceUpdateAvailable;

  const permissions = isShipChandlingWorkflowService
    ? "canApprovePortService"
    : ["canUpdatePortServices"];

  return (
    <FlowActionButton
      langBase={LANG_BASE}
      permission={permissions}
      isActionAvailable={isActionAvailable}
      labelKey="fields.update-button"
      testId="update-port-service"
      variant="outlined"
      startIcon={<AutorenewIcon />}
      onClick={() => openUpdateDrawer(portServiceData)}
    />
  );
};

export default UpdatePortService;
